/** Authentification sur le module `/auth` du backend. */
import { assetUrl, http, isOfflineError, orUndefined, refreshSession, storeImage, tokenStore } from "./http";
import { endOfflineSession, resumeOfflineSession, startOfflineSession } from "@/offline/session";
import { setForcedOffline } from "@/offline/status";
import type { AppUser, CycleScope, Role } from "@/types";

/** Réponse de `GET /auth/me`. */
export interface AuthMe {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  avatarUrl: string | null;
  role: { id: number; name: string; scope: CycleScope; cycleId: number | null; isSystem?: boolean };
  isActive: boolean;
  createdAt: string;
  /** Cycle auquel l'utilisateur est limité (null = tous les cycles). */
  cycleId: number | null;
  /** Permissions effectives du rôle. */
  permissions: string[];
  /** École de rattachement (null pour le super-administrateur de la plateforme). */
  schoolId: number | null;
  isPlatformAdmin: boolean;
  /** Mot de passe provisoire : à changer avant toute autre action. */
  mustChangePassword: boolean;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/** Profil prêt pour l'interface : utilisateur + rôle (avec ses permissions). */
export interface SessionProfile {
  user: AppUser;
  role: Role;
}

export function toSessionProfile(me: AuthMe): SessionProfile {
  const roleId = String(me.role.id);
  return {
    user: {
      id: String(me.id),
      firstName: me.firstName,
      lastName: me.lastName,
      email: me.email,
      phone: me.phone ?? undefined,
      avatar: assetUrl(me.avatarUrl) || undefined,
      roleId,
      roleName: me.role.name,
      cycleScope: me.role.scope,
      isActive: me.isActive,
      createdAt: me.createdAt.slice(0, 10),
      schoolId: me.schoolId === null ? undefined : String(me.schoolId),
      isPlatformAdmin: me.isPlatformAdmin,
      mustChangePassword: me.mustChangePassword,
    },
    role: {
      id: roleId,
      name: me.role.name,
      description: "",
      cycleScope: me.role.scope,
      permissions: me.permissions,
      isSystem: me.role.isSystem ?? false,
    },
  };
}

/** Profil du serveur, gardé sur l'appareil pour rouvrir l'espace sans réseau. */
export async function fetchProfile(): Promise<SessionProfile> {
  const me = await http.get<AuthMe>("/auth/me");
  await startOfflineSession(String(me.id), me);
  return toSessionProfile(me);
}

/** Connexion : enregistre les jetons puis renvoie le profil. */
export async function login(email: string, password: string, remember: boolean): Promise<SessionProfile> {
  const tokens = await http.post<AuthTokens>("/auth/login", { email, password }, { auth: false });
  tokenStore.save(tokens.accessToken, tokens.refreshToken, remember);
  return fetchProfile();
}

/**
 * Reprend la session au chargement de la page (jeton de renouvellement conservé). Sans réseau, l'espace
 * se rouvre avec le profil gardé, si la dernière connexion au serveur date de moins de 3 jours.
 */
export async function restoreSession(): Promise<SessionProfile | null> {
  if (!tokenStore.hasRefreshToken()) return null;
  const resumeOffline = async () => {
    const me = await resumeOfflineSession<AuthMe>();
    return me ? toSessionProfile(me) : null;
  };
  const refreshed = await refreshSession();
  if (refreshed === "offline") return resumeOffline();
  if (refreshed === "invalid") return null;
  try {
    return await fetchProfile();
  } catch (error) {
    if (isOfflineError(error)) return resumeOffline();
    tokenStore.clear();
    return null;
  }
}

/** Déconnexion : invalide la session côté serveur, oublie les jetons et efface les données de l'appareil. */
export async function logout(): Promise<void> {
  // Le mode hors ligne choisi s'arrête à la déconnexion : la session est aussi close sur le serveur si possible.
  setForcedOffline(false);
  try {
    await http.post<null>("/auth/logout");
  } catch {
    /* session déjà expirée ou hors ligne : rien à invalider */
  } finally {
    tokenStore.clear();
    await endOfflineSession();
  }
}

/**
 * Changement de son propre mot de passe. Le serveur révoque la session : l'utilisateur se reconnecte
 * avec le nouveau mot de passe.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  try {
    await http.post<null>("/auth/change-password", { currentPassword, newPassword });
  } finally {
    tokenStore.clear();
  }
}

/**
 * « Mot de passe oublié » : le serveur envoie un lien valable 1 heure. La réponse est la même que
 * l'adresse corresponde à un compte ou non.
 */
export async function forgotPassword(email: string): Promise<void> {
  await http.post<null>("/auth/forgot-password", { email: email.trim() }, { auth: false });
}

/** Nouveau mot de passe choisi depuis le lien reçu par e-mail (le lien ne sert qu'une fois). */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await http.post<null>("/auth/reset-password", { token, newPassword }, { auth: false });
}

/** Règle du serveur : 10 caractères minimum, une majuscule, une minuscule et un chiffre. */
export const STRONG_PASSWORD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,128}$/;
export const STRONG_PASSWORD_HINT = "10 caractères minimum, avec une majuscule, une minuscule et un chiffre";

/** Ce que l'utilisateur modifie lui-même. Téléphone ou photo vides = effacés. */
export interface ProfileDto {
  firstName: string;
  lastName: string;
  phone: string;
  /** Data URL choisie sur le poste, URL du serveur, ou vide. */
  avatar: string;
}

export async function updateProfile(dto: Partial<ProfileDto>): Promise<SessionProfile> {
  return toSessionProfile(
    await http.put<AuthMe>("/auth/me", {
      firstName: dto.firstName?.trim(),
      lastName: dto.lastName?.trim(),
      phone: dto.phone === undefined ? undefined : orUndefined(dto.phone) ?? null,
      avatarUrl: dto.avatar === undefined ? undefined : (await storeImage(dto.avatar)) ?? null,
    }),
  );
}
